/* global Vue, VeeValidate, VueI18n, VueRouter, LOYALTY_API */

import { setSuccessAndStopLoading, setErrorAndStopLoading, startLoading, stopLoading } from '../../apps/components/Notifications'
import { AuthService, fetchWithTokenAsync } from '../../AuthService'
import { formatNumber } from '../../utils-common'

export default {
  name: 'TransferPoints',
  template: `
<div class="loyalty-transfer-points section-margin-bottom">
  <div class="container">
    <div class="grid">
      <div class="col-12 col-md-10 offset-md-1 col-xl-8 offset-xl-2">
        <h2 v-html="$t('transferPoints.title')" ref="topOfPage" />
      </div>
    </div>
  </div>
  <form class="container grid no-padding-sm-down" ref="formElement">
    <div class="col-12 col-sm-6 col-lg-5 offset-lg-1 col-xl-4 offset-xl-2">
      <div class="loyalty-form__wrapper"
        :class="form.numberOfPointsType === 'all' ? 'loyalty-form__wrapper--dark' : ''">
        <div class="loyalty-transfer-points__amount loyalty-form__border">
          <label for="allPoints" class="label-radio">
            <input id="allPoints" name="numberOfPointsType" value="all" type="radio" v-model="form.numberOfPointsType" @blur="validateField('numberOfPointsType')">
            <span>{{ $t('transferPoints.allPointsSelection') }}</span>
          </label>
          <hr>
          <p class="no-margin-bottom">{{ $t('transferPoints.pointsAvailable') }} <span class="number">{{ formatNumber(availablePoints) }}</span></p>
        </div>
      </div>
    </div>

    <div class="col-12 col-sm-6 col-lg-5 col-xl-4">
      <div
        class="loyalty-form__wrapper"
        :class="form.numberOfPointsType === 'other' ? 'loyalty-form__wrapper--dark' : ''">
        <div class="loyalty-transfer-points__amount loyalty-form__border">
          <label for="points2transfer" class="label-radio">
            <input id="points2transfer" name="numberOfPointsType" value="other" type="radio" v-model="form.numberOfPointsType" @blur="validateField('numberOfPointsType')">
            <span>{{ $t('transferPoints.otherAmountSelection') }}</span>
          </label>
          <hr>
          <div class="loyalty-transfer-points__other-amount">
            <div>
              <p class="no-margin">{{ $t('transferPoints.points2Transfer') }}</p>
              <input type="number" min="1" :max="availablePoints" v-model="otherAmount" name="otherAmount" id="otherAmount" @keypress="validateNumberInput">
            </div>
            <p v-show="tooManyPoints" class="field-error">{{ $t('formValidation.maxPoints') }}</p>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 col-lg-10 offset-lg-1 col-xl-8 offset-xl-2">
      <div class="container loyalty-form loyalty-form__wrapper">
        <div class="loyalty-form__border">
          <span class="field-error">{{ formErrors.numberOfPointsType }}</span>

          <h4>{{ $t('transferPoints.formTitle') }}</h4>
          <hr>

          <div class="form-element">
            <label for="receiverFirstName">{{ $t('transferPoints.receiverFirstName') }}</label>
            <input id="receiverFirstName" name="receiverFirstName" type="text" v-model="form.receiverFirstName" @blur="validateField('receiverFirstName')">
            <span class="field-error">{{ formErrors.receiverFirstName }}</span>
          </div>

          <div class="form-element">
            <label for="receiverLastName">{{ $t('transferPoints.receiverLastName') }}</label>
            <input id="receiverLastName" name="receiverLastName" type="text" v-model="form.receiverLastName" @blur="validateField('receiverLastName')">
            <span class="field-error">{{ formErrors.receiverLastName }}</span>
          </div>

          <div class="form-element">
            <label for="receiverLoyaltyId">{{ $t('transferPoints.receiverLoyaltyId') }}</label>
            <input id="receiverLoyaltyId" name="receiverLoyaltyId" type="number" min="0" v-model.number="form.receiverLoyaltyId" @blur="validateField('receiverLoyaltyId')">
            <span class="field-error">{{ formErrors.receiverLoyaltyId }}</span>
          </div>

          <div class="form-element">
            <label for="receiverEmail">{{ $t('transferPoints.receiverEmail') }}</label>
            <input id="receiverEmail" name="receiverEmail" type="email" v-model="form.receiverEmail" @blur="validateField('receiverEmail')">
            <span class="field-error">{{ formErrors.receiverEmail }}</span>
          </div>

          <p class="no-margin"><i>{{ $t('activityPointsCommon.requiredFields') }}</i></p>
        </div>

        <div class="button-group">
          <button class="button button--outline" @click="$router.push({ name: 'PointsActivity' })">{{ $t('cancel') }}</button>
          <button class="button button--maistar" @click.prevent="submitForm">{{ $t('activityPointsCommon.send') }}</button>
        </div>
      </div>
    </div>
  </form>
</div>`,
  emits: ['error', 'success'],
  props: {
    availablePoints: Number,
    loyaltyId: Number,
  },
  setup (props, { emit }) {
    const router = VueRouter.useRouter()
    const form = Vue.reactive({})
    const formElement = Vue.ref(null)
    const formErrors = Vue.reactive({})
    const availablePoints = Vue.ref(0)
    const otherAmount = Vue.ref(1)
    const topOfPage = Vue.ref(null)
    const tooManyPoints = Vue.ref(false)
    const { t } = VueI18n.useI18n()
    const { loyaltyId } = Vue.toRefs(props)
    const validationSchema = {
      numberOfPointsType: 'required',
      receiverFirstName: 'required',
      receiverLastName: 'required',
      receiverEmail: 'required|email',
      receiverLoyaltyId: 'reqNumber',
    }

    const { handleSubmit, handleReset, errors, isSubmitting } = VeeValidate.useForm({
      validationSchema,
      initialValues: {
        numberOfPointsType: 'other',
      },
    })

    const fieldRefs = Vue.reactive({})
    Object.keys(validationSchema).forEach((field) => {
      const fieldObj = VeeValidate.useField(field, undefined, {
        validateOnValueUpdate: false,
      })
      form[field] = fieldObj.value
      formErrors[field] = fieldObj.errorMessage
      fieldRefs[field] = fieldObj
    })

    Vue.watch(isSubmitting, () => {
      if (Object.keys(errors).length > 0 && formElement.value) {
        const highlight = Array.from(formElement.value.querySelectorAll('.field-error')).filter(er => er.innerHTML)[0] || null
        if (highlight) { highlight.parentNode.scrollIntoView({ behavior: 'smooth' }) }
      }
    })

    function validateNumberInput (e) {
      const keyCode = e.keyCode ? e.keyCode : e.which
      // Allow only numbers (0-9) and special keys like backspace, delete, arrows
      if ((keyCode < 48 || keyCode > 57) && // 0-9
          keyCode !== 8 && // Backspace
          keyCode !== 9 && // Tab
          keyCode !== 46 && // Delete
          keyCode !== 37 && // Left arrow
          keyCode !== 39) { // Right arrow
        e.preventDefault()
        return false
      }
      return true
    }

    const submitForm = handleSubmit(async () => {
      let points2transfer = 0
      if (form.numberOfPointsType === 'all') {
        points2transfer = availablePoints.value
      } else {
        if (otherAmount.value > availablePoints.value) {
          tooManyPoints.value = true
          return
        }
        points2transfer = otherAmount.value
      }
      try {
        startLoading('transfer-points')
        const postJson = Object.assign({}, form, {
          requestType: 2,
          numberOfPointsToTransfer: points2transfer,
        })
        postJson.receiverLoyaltyId = postJson.receiverLoyaltyId?.toString()
        const response = await fetchWithTokenAsync(`${LOYALTY_API}/api/v1/loyaltyrequests`, AuthService.accessToken, 'POST', postJson)
        if (!response?.value || response.value.length < 5) {
          throw Error('TransferPoints')
        }
        form.receiverLoyaltyId = ''
        handleReset()
        setSuccessAndStopLoading({ details: t('transferPoints.success'), code: 'transferPoints' })
        router.push({ name: 'PointsActivity' })
      } catch (err) {
        setErrorAndStopLoading(err)
      }
    })

    function validateField (field) {
      fieldRefs[field].validate()
    }

    Vue.watch(loyaltyId, async () => {
      try {
        startLoading('transfer-points-mount')
        const response = await fetchWithTokenAsync(`${LOYALTY_API}/api/v1/MyLoyaltyStatus`, AuthService.accessToken, 'GET')
        if (!response?.loyaltyProfile) {
          throw Error('MyLoyaltyStatus')
        }
        availablePoints.value = response.loyaltyProfile.availablePoints
        stopLoading('transfer-points-mount')
      } catch (err) {
        setErrorAndStopLoading(err)
      }
    }, { immediate: true })

    return {
      form,
      formElement,
      formErrors,
      submitForm,
      availablePoints,
      otherAmount,
      tooManyPoints,
      topOfPage,
      formatNumber,
      validateField,
      validateNumberInput,
    }
  },
}
